import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { autoUpdate } from '@pypestream/floating-ui-dom';
import { property, query, state } from 'lit/decorators.js';
import { nanoid } from 'nanoid';
import { signal, Signal } from '@lit-labs/preact-signals';
import { ClickAwayController, FocusTrapController } from '../../../internal';
import { BaseElement, PSCustomEvent } from '../../base-element';
import { MenuItemSelectedEvent, MenuItemWC } from '../../menu/src/menu-item.wc';
import {
  PortalDestinationWC,
  PortalReadyEvent,
} from '../../portal/portal-destination.wc';
import { PageWC } from '../../page';

export type PopoverAutoCloseEvent = PSCustomEvent<
  // eslint-disable-next-line no-use-before-define
  PopoverBaseWC,
  MouseEvent
>;

declare global {
  interface GlobalEventHandlersEventMap {
    'popover-autoclose': PopoverAutoCloseEvent;
  }
}

export const popoverItemsSignal: Signal<string[]> = signal<string[]>([]);

export class PopoverBaseWC extends BaseElement {
  onClickAway = new ClickAwayController({
    host: this,
  });

  focusTrapController = new FocusTrapController(
    this,
    true,
    () => this.preventScroll
  );

  private triggerEl: Element | null;

  private parentEl: Element | null;

  private cleanup: ReturnType<typeof autoUpdate> | undefined;

  private mutationObserver: MutationObserver;

  @query('#popover-container') containerEl: HTMLElement;

  @query('#popover-arrow') arrowEl: HTMLElement;

  /**
   * The element id the popover will be anchored to
   */
  @property() trigger: string | Element;

  /**
   * Add event listeners to the trigger element.
   */
  @property({ type: Boolean, attribute: 'disable-trigger-listeners' })
  disableTriggerListeners: boolean = false;

  /**
   * The trigger action type which executes the popover action.
   */
  @property({ reflect: true, attribute: 'trigger-action' }) triggerAction:
    | 'click'
    | 'click-and-hover'
    | 'context' = 'click';

  /**
   * Activates the positioning logic and shows the popover. When this attribute is removed, the positioning logic is torn
   * down and the popup will be hidden.
   */
  @property({ type: Boolean, reflect: true }) active = false;

  @property({ reflect: false }) actionType:
    | 'hover'
    | 'click'
    | 'focus'
    | 'keyboard'
    | 'unknown' = 'unknown';

  /**
   * The preferred UI width
   */
  @property({ reflect: true }) width: 'default' | 'auto' = 'default';

  /**
   * The preferred placement of the popup. Note that the actual placement will vary as configured to keep the
   * panel inside of the viewport.
   */
  @property({ reflect: true }) placement:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end' = 'top';

  /** Attaches an arrow to the popup. */
  @property({ type: Boolean }) hasArrow = false;

  /**
   * The amount of padding between the arrow and the edges of the popup. If the popup has a border-radius, for example,
   * this will prevent it from overflowing the corners.
   */
  @property({ attribute: 'arrow-padding', type: Number }) arrowPadding = 10;

  /** The distance in pixels from which to offset the panel away from its anchor. */
  @property({ type: Number }) distance = 0;

  /** The distance in pixels from which to offset the panel along its anchor. */
  @property({ type: Number }) skidding = 0;

  /**
   * When set, placement of the popup will flip to the opposite site to keep it in view.
   */
  @property({ type: Boolean }) flip = true;

  /** Moves the popup along the axis to keep it in view when clipped. */
  @property({ type: Boolean }) shift = true;

  /**
   * Hides popover on click outside
   */
  @property({ type: Boolean, reflect: true }) closeOnClickOutside = true;

  /**
   * Hides popover on esc btn click
   */
  @property({ type: Boolean, reflect: true }) closeOnEscClick = true;

  /**
   * Skips using the built-in portal and instead
   */
  @property({ type: Boolean, reflect: true, attribute: 'omit-portal' })
  omitPortal = false;

  @property({ type: Boolean, reflect: true, attribute: 'disable-focus-trap' })
  disableFocusTrap = false;

  /**
   * PSTUDIO-4033 — opt in to a focus trap that never scrolls the viewport.
   *
   * The trap hands focus back to its trigger ~500ms after the popover closes.
   * When the app deliberately scrolled somewhere else in the meantime (the
   * workflows table revealing a freshly inserted block, PSTUDIO-3848), that
   * late `focus()` makes the browser scroll the trigger back — centering it,
   * measured at 476px — and the reveal is undone.
   *
   * Scoped to the RETURN alone — the initial focus, the pull-back when focus
   * escapes and the Tab wrap at a group boundary all keep scrolling natively,
   * so focus can never end up alive but off-screen (WCAG 2.4.7). That scoping
   * is why `FocusTrapController` does not hand this to `focus-trap`'s own
   * `preventScroll` option, which would suppress every one of those moves; a
   * popover routinely outgrows its `max-height` and scrolls internally, so the
   * wrap in particular must stay visible.
   *
   * OFF by default: turn it on only where the consumer performs its own
   * deliberate scroll that the late return would otherwise undo.
   */
  @property({ type: Boolean, reflect: true, attribute: 'prevent-scroll' })
  preventScroll = false;

  /**
   * Enables / disables automatically closing a Popover when a nested menu item has been selected
   */
  @property({ type: Boolean, reflect: true }) stayOpenOnSelect = false;

  @property({ type: Boolean, reflect: false }) defaultActive = false;

  @property({ type: Boolean, reflect: false }) isFocused = false;

  @property({ reflect: false, attribute: false }) projectedContent = this;

  @property({ type: Boolean, reflect: true }) disabled: boolean = false;

  /**
   * Rendering performance improvement. When enabled, the Popover will immediately move to the projected portal as soon as possible (even when closed) + not move back to the original container when closed
   */
  @property({ type: Boolean, reflect: true, attribute: 'keep-mounted' })
  keepMounted = false;

  /** Computed position of poopver container */
  @state() transform: string;

  constructor() {
    super();
    this.onPortalReady = this.onPortalReady.bind(this);

    this.onClick = this.onClick.bind(this);
    this.onContextMenu = this.onContextMenu.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
  }

  async connectedCallback() {
    super.connectedCallback();

    if (this.parentElement && !this.parentEl) {
      // Keep the reference of parent element to append `this` instance of popover once portal is closed.
      this.parentEl = this.parentElement;
    }

    this.focusTrapController.hostConnected();
    this.handleKeyDown = this.handleKeyDown.bind(this);

    if (this.closeOnClickOutside) {
      this.onClickAway.excludeElement = this.triggerEl as HTMLElement;
    }

    document.addEventListener('portal-destination-ready', this.onPortalReady);
  }

  // eslint-disable-next-line class-methods-use-this
  onPortalReady(e: PortalReadyEvent) {
    if (e.detail.name && e.detail.name === this.popoverId) {
      // console.log(
      //   'this component portal is ready',
      //   e.detail.name,
      //   this.popoverId,
      //   this.keepMounted
      // );

      if (!this.omitPortal && this.keepMounted) {
        this.handleProjectContent(true);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.focusTrapController?.hostDisconnected();
    this.stop();
    this.removeOpenListeners();
    this.mutationObserver?.disconnect();

    document.removeEventListener(
      'portal-destination-ready',
      this.onPortalReady
    );

    this.removeEventListener('click', this.onClick);
    this.removeEventListener('contextMenu', this.onContextMenu);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('mouseout', this.onMouseOut);
    this.removeEventListener('mouseover', this.onMouseOver);
  }

  firstUpdated() {
    this.onClickAway.element = this.containerEl;
    this.onClickAway.callback = (e) => this.handleClickOutside(e);

    const idAttribute = this.getAttribute('id');
    if (idAttribute && !this.popoverId) {
      this.popoverId = idAttribute;
    } else if (!this.popoverId) {
      this.popoverId = `popover-${nanoid()}`;
    }

    if (this.defaultActive) {
      this.show();
      this.isFocused = true;
    }

    // if (this.projectionTrigger === 'first-updated') {
    //   console.log('first-updated', this.popoverId);
    //   this.handleProjectContent(true);
    // }
  }

  private handleItemSelected = (event: MenuItemSelectedEvent) => {
    event.stopPropagation();
    const target = event.target as MenuItemWC;
    const activeElId = popoverItemsSignal.value.at(-1);

    // Hide the dropdown when a menu item is selected
    if (
      !this.stayOpenOnSelect &&
      target.tagName.toLowerCase().includes('ps-menu') &&
      this.popoverId === activeElId
    ) {
      this.hide(event, 'handleItemSelected');
    }
  };

  addOpenListeners() {
    if (this.closeOnEscClick) {
      document.addEventListener('keydown', this.handleKeyDown);
    }

    this.addEventListener('menu-item-selected', this.handleItemSelected);
  }

  removeOpenListeners() {
    if (this.closeOnEscClick) {
      document.removeEventListener('keydown', this.handleKeyDown);
    }

    this.removeEventListener('menu-item-selected', this.handleItemSelected);
    this.removeEventListener('mouseout', this.hide);
    this.removeEventListener('blur', this.hide);
  }

  handleAutoClose(event: KeyboardEvent | MouseEvent) {
    this.emit('popover-autoclose', {
      detail: event,
    }) as PopoverAutoCloseEvent;
  }

  handleKeyDown(event: KeyboardEvent) {
    // eslint-disable-next-line no-console
    console.log('handleKeyDown', this.active, event.key, this.closeOnEscClick);
    // Close when escape is pressed inside an open dropdown.
    if (this.active && event.key === 'Escape' && this.closeOnEscClick) {
      // if (this.contains(document.activeElement)) {
      event.stopPropagation();
      this.hide(event, 'handleKeyDown');
      this.handleAutoClose(event);
      this.isFocused = false;
      // }
    }
  }

  handleClickOutside(e: MouseEvent) {
    if (!this.active) return;

    this.handleAutoClose(e);

    const activeElId = popoverItemsSignal.value.at(-1);

    if (this.closeOnClickOutside && this.id === activeElId) {
      this.hide(e, 'handleClickOutside');
    }
  }

  handleProjectContent(project: boolean) {
    if (project) {
      this.emit('update-portal-content', {
        detail: {
          destination: this.popoverId,
          content: this.projectedContent,
        },
      });
    } else {
      // console.log('close popover', this.parentEl);
      // Close projecting popover.
      this.emit('update-portal-content', {
        detail: {
          content: '',
          destination: this.popoverId,
        },
      });
      // Append this instance back to parent element.
      if (this.parentEl && !this.parentEl.contains(this.projectedContent)) {
        this.parentEl.append(this.projectedContent);
      }
    }
  }

  hide(e?: Event, reason: string = 'unknown') {
    if (this.active) {
      this.active = false;
      this.emit('close');
      this.isFocused = false;
      this.triggerEl?.removeAttribute('data-popover-open');
    }
  }

  show() {
    this.active = true;
    this.emit('popover-open');
    this.reposition();
    this.triggerEl?.setAttribute('data-popover-open', '');
  }

  async updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps);

    // Update the anchor when trigger changes
    if (changedProps.has('trigger')) {
      await this.handleTriggerChange();
    }

    if (this.trigger && !this.triggerEl) {
      if (this.trigger && typeof this.trigger === 'string') {
        this.triggerEl = document.getElementById(this.trigger);
      }
    }

    if (this.disabled && changedProps.has('disabled')) {
      this.hide();
      this.disconnectedCallback();
    }

    // Update the anchor when trigger changes
    // Start or stop the positioner when active changes
    if (changedProps.has('active')) {
      if (this.active) {
        if (!this.omitPortal && !this.keepMounted) {
          this.handleProjectContent(true);
        }

        if (!this.disableFocusTrap) {
          setTimeout(() => {
            this.focusTrapController.activate();
          }, 10);
        }

        this.start();
        this.addOpenListeners();
        popoverItemsSignal.value = [
          ...popoverItemsSignal.value,
          this.popoverId,
        ];

        if (this.triggerEl?.parentNode) {
          this.mutationObserver = new MutationObserver(
            this.handleTriggerMutation
          );
          this.mutationObserver.observe(this.triggerEl.parentNode, {
            childList: true,
            subtree: true,
          });
        }
      } else if (
        popoverItemsSignal.value.at(-1) === this.popoverId &&
        !this.active
      ) {
        this.focusTrapController.deactivate();
        if (!this.omitPortal && !this.keepMounted) {
          this.handleProjectContent(false);
        }
        this.stop();
        this.removeOpenListeners();
        this.mutationObserver?.disconnect();
        popoverItemsSignal.value = popoverItemsSignal.value.filter(
          (id) => id !== this.popoverId
        );
      }
    }

    // If we don't have parentElement available/connected, we should toggle popover back to disabled state.
    // This is to account route change or scenario, where parent element gets removed from html tree.
    // if (!this.parentEl?.isConnected || !this.parentEl?.checkVisibility()) {
    //   this.active = false;
    // }
  }

  @property({ type: String, reflect: true, attribute: 'popover-id' })
  popoverId: string;

  private handleTriggerMutation = (mutations: MutationRecord[]) => {
    for (const mutation of mutations) {
      if ([...mutation.removedNodes].includes(this.triggerEl as Node)) {
        this.focusTrapController?.hostDisconnected();
        this.hide(undefined, 'handleTriggerMutation');

        this.stop();
        this.removeOpenListeners();
      }
    }
  };

  onClick() {
    if (!this.active) {
      this.show();
    } else {
      if (this.isFocused) return;
      this.hide();
    }
  }

  onFocusOut() {
    this.isFocused = false;
    this.hide();
  }

  onFocusIn() {
    this.isFocused = true;
    this.show();
  }

  onContextMenu(e: Event) {
    e.preventDefault();
    e.stopImmediatePropagation();

    if (!this.active) {
      this.show();
    } else {
      if (this.isFocused) return;
      this.hide();
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.which === 32 || e.which === 13 || e.key === 'Enter') {
      if (this.isFocused) return;

      e.preventDefault();

      if (!this.active) {
        this.show();
      } else {
        if (this.isFocused) return;
        this.hide();
      }
    }
  }

  onMouseOut() {
    if (this.isFocused) return;
    this.hide();
  }

  onMouseOver() {
    if (this.active) return;
    if (this.isFocused) return;
    this.show();
    this.triggerEl?.addEventListener('mouseout', this.onMouseOut);
  }

  private async handleTriggerChange(): Promise<void> {
    return new Promise((resolve) => {
      this.stop().then(() => {
        // temp workaround till we can tighten this up
        try {
          if (this.trigger && typeof this.trigger === 'string') {
            // Locate the anchor by id
            this.triggerEl = document.getElementById(this.trigger);

            if (!this.triggerEl) {
              this.triggerEl = (
                this.getRootNode() as ShadowRoot
              )?.getElementById(this.trigger);
            }
          } else if (this.trigger instanceof Element) {
            // Use the anchor's reference
            this.triggerEl = this.trigger;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            'Invalid anchor element: no anchor could be found using the anchor slot or the anchor attribute.'
          );
        }

        if (!this.triggerEl) {
          // throw new Error(
          //   'Invalid anchor element: no anchor could be found using the anchor slot or the anchor attribute.'
          // );
        } else {
          if (!this.disableTriggerListeners) {
            if (
              this.triggerAction === 'click' ||
              this.triggerAction === 'click-and-hover'
            ) {
              if (this.triggerAction === 'click') {
                // automatically add click event bindings to trigger
                this.triggerEl?.addEventListener('click', this.onClick);
              } else if (this.triggerAction === 'click-and-hover') {
                this.triggerEl?.addEventListener('mouseover', this.onMouseOver);
                this.triggerEl?.addEventListener('focusin', this.onFocusIn);
                this.triggerEl?.addEventListener('focusout', this.onFocusOut);
              }

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              this.triggerEl?.addEventListener('keydown', this.onKeyDown);

              this.onClickAway.excludeElement = this.triggerEl as HTMLElement;
            } else {
              this.triggerEl?.addEventListener(
                'contextmenu',
                this.onContextMenu
              );
            }
          }

          this.popoverId =
            this.popoverId || this.getAttribute('id') || `popover-${nanoid()}`;
          this.setAttribute('id', this.popoverId);
          this.triggerEl.setAttribute('data-popover-id', this.popoverId);

          if (this.omitPortal) return;

          const rootElement =
            document.querySelector('ps-page') !== null
              ? (document.querySelector('ps-page') as PageWC)
              : document.body;

          const portalDestinationEl = document.querySelector(
            `${PortalDestinationWC.tagname}[name="${this.popoverId}"]`
          );

          if (!portalDestinationEl) {
            const destinationEl = document.createElement(
              PortalDestinationWC.tagname
            );

            destinationEl.setAttribute('name', this.popoverId);
            rootElement.appendChild(destinationEl);
          }
        }

        resolve();
      });
    });
  }

  private start() {
    // We can't start the positioner without an anchor
    if (!this.triggerEl) {
      return;
    }

    this.cleanup = autoUpdate(this.triggerEl, this.containerEl, () => {
      this.reposition();
    });
  }

  private async stop(): Promise<void> {
    return new Promise((resolve) => {
      if (this.cleanup) {
        this.cleanup();
        this.cleanup = undefined;
        this.removeAttribute('data-current-placement');
        requestAnimationFrame(() => resolve());
      } else {
        resolve();
      }
    });
  }

  /** Forces the popover to recalculate and reposition itself. */
  reposition() {
    // Nothing to do if the popover is inactive or the trigger doesn't exist
    if (!this.active) return;

    if (!this.triggerEl || !this.triggerEl?.isConnected) {
      setTimeout(() => {
        if (!this.triggerEl || !this.triggerEl?.isConnected) {
          this.hide(undefined, 'no trigger');
        }
      }, 100);

      return;
    }

    //
    // NOTE: Floating UI middlewares are order dependent: https://floating-ui.com/docs/middleware
    //
    const middleware = [
      // The offset middleware goes first
      offset({ mainAxis: this.distance, crossAxis: this.skidding }),
    ];

    if (this.flip) {
      middleware.push(
        flip({
          fallbackAxisSideDirection: 'start',
          crossAxis: !this.shift,
        })
      );
    }

    if (this.shift) {
      middleware.push(
        shift({
          padding: 16,
        })
      );
    }

    if (this.hasArrow) {
      middleware.push(
        arrow({
          element: this.arrowEl,
          padding: this.arrowPadding,
        })
      );
    }

    computePosition(this.triggerEl, this.containerEl, {
      placement: this.placement,
      middleware,
      strategy: 'fixed',
    }).then(({ x, y, middlewareData, placement }) => {
      this.setAttribute('data-current-placement', placement);

      this.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;

      if (this.hasArrow && middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        const staticSide: string = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[placement.split('-')[0]] as string;

        Object.assign(this.arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });
      }
    });
  }
}
